import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
// @ts-ignore
import VideoThumbnail from 'react-video-thumbnail';

import { Popover } from '../../../../components/general';
import { SideIcon } from '../../../../components/icons';

import {
  getThumbnailSize,
  getVideoName,
} from '../../../../components/video/getVideoContainerData';
import { useProjectStore } from '../../../../stores/project';
import { useUserStore } from '../../../../stores/user';
import { useVideoBuilderStore } from '../../../../stores/videoBuilder';

import { AnalysisFile } from '../../../../types';
import { getThumbnail } from '../../../../utils';

import { ClipPreviewController } from '../../components/clipPreview';
import ThumbnailStatus from './thumbnailStatus';

interface Props {
  clip: AnalysisFile;
  onDelete: () => void;
  previewRef: React.RefObject<ClipPreviewController | null>;
}

const AnalysisClipBlock: React.FC<Props> = (props) => {
  const { clip, onDelete, previewRef } = props;

  const { adminMode } = useUserStore();
  const { deletingClipsIds } = useVideoBuilderStore();

  const isPortrait =
    useProjectStore((state) => state.currentProject?.videoMode) === 'portrait';

  const [thumbnailImg, setThumbnailImg] = useState<string | null>(null);

  const onMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      previewRef.current?.show({
        src: clip.videoUrl,
        startTime: 0,
        endTime: null,
        targetElement: e.currentTarget as HTMLElement,
      });
    },
    [previewRef, clip.videoUrl],
  );

  const onMouseLeave = useCallback(() => {
    previewRef.current?.hide();
  }, [previewRef]);

  const { thumbWidth, thumbAspect } = getThumbnailSize(isPortrait);
  const clipName = getVideoName(clip.fileName);

  const thumbnailClass = classNames(
    'w-full rounded-md object-cover',
    thumbAspect,
  );

  return (
    <div className={classNames('block', thumbWidth)} key={clip.elementId}>
      <button
        className={classNames(
          thumbnailClass,
          'group relative transition-all duration-300 hover:scale-110',
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* img overlay – contains delete & status icons, duration and checkbox */}
        <div
          className={classNames(
            thumbnailClass,
            'absolute flex flex-col items-center justify-center p-1 text-center',
            { 'bg-black-overlay-200': clip.status !== 'pending' },
          )}
        >
          {/* delete icon – shows X or spinner if deleting */}
          <SideIcon
            type="delete"
            deleteType="clip"
            isProcessing={deletingClipsIds.includes(clip.elementId)}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          />

          {/* clip status */}
          <ThumbnailStatus status={clip.status} />
        </div>

        {/* gets thumbnail from clip and saves it in state */}
        {(!clip.thumbnailUrl || isPortrait) && (
          <VideoThumbnail
            videoUrl={clip.videoUrl}
            thumbnailHandler={(thumbnail: string) => setThumbnailImg(thumbnail)}
            renderThumbnail={false}
          />
        )}

        {/* thumbnail image */}
        <img
          src={thumbnailImg ?? clip.thumbnailUrl ?? getThumbnail(null)}
          className={thumbnailClass}
          alt={`${clipName} Thumbnail`}
        />
      </button>

      {/* file name */}
      <div className="flex flex-col mt-1 gap-1 text-xs leading-none break-words">
        <div>{clipName}</div>

        {adminMode && (
          <Popover
            placement="bottom"
            body={
              <div className="flex flex-col gap-1 text-xs leading-none">
                <div>
                  <span className="font-bold">ID</span>: {clip.elementId}
                </div>
              </div>
            }
          >
            <div className="w-min text-primary-900 underline whitespace-nowrap cursor-pointer">
              More Info
            </div>
          </Popover>
        )}
      </div>
    </div>
  );
};

export default AnalysisClipBlock;
